import React, { useEffect, useState } from "react"
import { SWRConfig } from "swr"
import { useLocation } from "react-router"
import { ReportsProtectedRoute } from "@/components/reports/auth/ReportsProtectedRoute"
import { PendingActivationCard } from "@/components/reports/auth/PendingActivationCard"
import { AppSidebar } from "@/components/reports/AppSidebar"
import { NavHeader } from "@/components/reports/NavHeader"
import { NotificationSystem } from "@/components/reports/NotificationSystem"
import { UnreadNotesProvider } from "@/contexts/UnreadNotesContext"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { BackgroundJobsPanel } from "@/components/jobs/BackgroundJobsPanel"
import { CommandPalette } from "@/components/search/CommandPalette"
import { FeedbackBubble } from "@/components/feedback/FeedbackBubble"
import { useAuth } from "@/hooks/useAuth"
import { useUsageTracking } from "@/hooks/useUsageTracking"
import { useReportsStore } from "@/stores/reportsStore"
import { useOrganizationStore } from "@/stores/organizationStore"
import { PENDING_ROLE } from "@/types/reports"

const swrConfig = {
	revalidateOnFocus: false,
	revalidateOnReconnect: true,
	dedupingInterval: 10000,
	focusThrottleInterval: 120000,
	errorRetryCount: 2,
	keepPreviousData: true,
}

interface DashboardLayoutProps {
	children: React.ReactNode
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
	const { reportsUser, isAuthenticated } = useAuth()
	const { pathname } = useLocation()
	const { fetchReports } = useReportsStore()
	const { fetchAllOrganizations } = useOrganizationStore()
	const [paletteOpen, setPaletteOpen] = useState(false)

	// PENDING seats (bootstrap role, zero permissions) see a waiting card on
	// every route until an org admin assigns a real role. Case-insensitive —
	// legacy rows may store the value with different casing.
	const isPendingUser = (reportsUser?.role ?? "").toUpperCase() === PENDING_ROLE
	useUsageTracking(isAuthenticated && !!reportsUser && !isPendingUser)

	useEffect(() => {
		if (!isAuthenticated) return
		if (isPendingUser) return // zero permissions — these fetches would only 403
		fetchReports()
		fetchAllOrganizations()
	}, [fetchReports, fetchAllOrganizations, reportsUser?.organizationId, isAuthenticated, isPendingUser])

	// Global Cmd+K / Ctrl+K → open command palette
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault()
				setPaletteOpen((v) => !v)
			}
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [])

	return (
		<SWRConfig value={swrConfig}>
			<ReportsProtectedRoute>
				<UnreadNotesProvider userId={reportsUser?.id} userRole={reportsUser?.role} userOrganizationId={reportsUser?.organizationId}>
					<SidebarProvider defaultOpen={false}>
						<AppSidebar />
						<SidebarInset className='overflow-hidden'>
							<NavHeader />
							{/*
							  The app's real scrollport. Its TOP padding lives on the inner
							  wrapper, not here, so that `position: sticky; top: 0` inside a page
							  parks flush against the visible top edge.
							  Sticky offsets resolve against a scrollport's PADDING edge, so a
							  `p-8` here would park every sticky header 32px down and let content
							  scroll through the gap above it.
							*/}
							<div className='h-[calc(100vh-3.5rem)] overflow-y-auto px-4 pb-4 md:px-6 md:pb-6 lg:px-8 lg:pb-8'>
								<ErrorBoundary resetKey={pathname}>
									{isPendingUser ? (
										<PendingActivationCard />
									) : (
										<div className="flex flex-col gap-4 md:gap-8 h-full pt-4 md:pt-6 lg:pt-8">{children}</div>
									)}
								</ErrorBoundary>
							</div>
						</SidebarInset>
						{!isPendingUser && <NotificationSystem />}
						{!isPendingUser && <BackgroundJobsPanel />}
						{!isPendingUser && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
						{!isPendingUser && <FeedbackBubble />}
					</SidebarProvider>
				</UnreadNotesProvider>
			</ReportsProtectedRoute>
		</SWRConfig>
	)
}
